import React, { useEffect, useMemo, useState } from 'react'
import {
  PiTrendUp,
  PiTrophy,
  PiTarget,
  PiCurrencyDollar,
  PiCaretDown,
} from 'react-icons/pi'
import { cn } from '../UI/cn'
import { Tooltip } from '../Tooltip/tooltip'
import {
  getPerformanceStats,
  getHomepageStats,
  type PerformanceStats,
  type PerformanceRange,
  type HomepageStats,
} from '../../services/action_summary_api'
import {
  formatInteger,
  formatPercentage,
  formatCurrency,
} from '../../utils/formatting'
import { getChartColor } from '../../utils/chartColors'
import { PerformanceChart } from './PerformanceChart'
import { ProgressChart } from '../NewCompanyPage/Sidebar/shared/ProgressChart'
import type { PeriodType } from './PeriodToggle'
import { RangeSelector } from './RangeSelector'

/** Shared card container styles used across homepage dashboard cards. */
const CARD_CONTAINER_STYLES = 'bg-white rounded-lg border border-gray-200 p-4'

interface PerformanceDashboardCardProps {
  /** Optional user ID for superusers to view another user's performance. */
  userId?: number
  /** Optional additional CSS class names. */
  className?: string
}

/**
 * Skeleton loader for the performance dashboard.
 *
 * Displays placeholder elements matching the card structure for smooth
 * loading transitions without content layout shift.
 */
const PerformanceDashboardSkeleton: React.FC<{ className?: string }> = ({
  className,
}) => (
  <div className={cn(CARD_CONTAINER_STYLES, className)}>
    <div className="animate-pulse space-y-4">
      {/* Header skeleton */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-4 w-24 bg-gray-200 rounded" />
          <div className="h-5 w-28 bg-gray-200 rounded" />
        </div>
        <div className="h-6 w-32 bg-gray-200 rounded" />
      </div>

      {/* Summary metrics skeleton */}
      <div className="grid grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-gray-50 rounded p-3 text-center space-y-1">
            <div className="h-6 w-10 bg-gray-200 rounded mx-auto" />
            <div className="h-3 w-16 bg-gray-100 rounded mx-auto" />
          </div>
        ))}
      </div>

      {/* Chart skeleton */}
      <div className="h-64 bg-gray-50 rounded" />
    </div>
  </div>
)

/**
 * Summary metric card displaying a single performance KPI with tooltip.
 *
 * Used to show key totals like deals opened, deals won, win rate, and emails sent.
 */
interface MetricCardProps {
  /** Icon element displayed above the value. */
  icon: React.ReactNode
  /** Formatted metric value (e.g., "1,234" or "65.5%"). */
  value: string
  /** Short label describing the metric. */
  label: string
  /** Title shown in the tooltip popup. */
  tooltipTitle: string
  /** Detailed description shown in the tooltip popup. */
  tooltipContent: string
}

const MetricCard: React.FC<MetricCardProps> = ({
  icon,
  value,
  label,
  tooltipTitle,
  tooltipContent,
}) => (
  <Tooltip
    title={tooltipTitle}
    content={tooltipContent}
    placement="top"
    delay={[300, 0]}
  >
    <div className="bg-gray-50 rounded-lg p-3 text-center cursor-help">
      <div className="flex items-center justify-center gap-1.5 text-gray-600 mb-1">
        {icon}
      </div>
      <div className="text-xl font-bold text-gray-900 leading-tight">
        {value}
      </div>
      <div className="text-[11px] text-gray-500 mt-0.5">{label}</div>
    </div>
  </Tooltip>
)

/**
 * Performance dashboard showing historical metrics over time.
 *
 * Layout:
 * 1. Header with range selector and period toggle (Monthly/Weekly)
 * 2. Summary metrics for selected range (Deals Opened, Won, Win Rate, Value Opened, Value Won, Emails Sent)
 * 3. Bar chart with tabs (Deals, Emails, Value) showing trends over the selected time window
 */
export const PerformanceDashboardCard: React.FC<
  PerformanceDashboardCardProps
> = ({ userId, className }) => {
  const [stats, setStats] = useState<PerformanceStats | null>(null)
  const [pipelineStats, setPipelineStats] = useState<HomepageStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [periodType] = useState<PeriodType>('monthly')
  const [range, setRange] = useState<PerformanceRange>('6m')
  const [showPipelineSnapshot, setShowPipelineSnapshot] = useState(false)

  useEffect(() => {
    let isCancelled = false

    const fetchStats = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const [perfData, pipelineData] = await Promise.all([
          getPerformanceStats({ userId, periodType, range }),
          getHomepageStats(userId),
        ])
        if (!isCancelled) {
          setStats(perfData)
          setPipelineStats(pipelineData)
        }
      } catch (err) {
        if (!isCancelled) {
          const message =
            err instanceof Error
              ? err.message
              : 'Failed to load performance data'
          setError(message)
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false)
        }
      }
    }

    fetchStats()

    return () => {
      isCancelled = true
    }
  }, [userId, periodType, range])

  // Transform stage data for pipeline chart (must be before early returns)
  const pipelineChartItems = useMemo(() => {
    if (!pipelineStats) return []

    const stagesWithDeals = pipelineStats.deals_by_stage.filter(
      (s) => s.count > 0
    )
    const totalDeals = stagesWithDeals.reduce((sum, s) => sum + s.count, 0)

    return stagesWithDeals.map((stage, index) => ({
      name: `${stage.stage_name} · ${formatCurrency(stage.total_value)} · ${stage.count} deal${stage.count !== 1 ? 's' : ''}`,
      value: stage.count,
      color: getChartColor(index),
      percentage:
        totalDeals > 0 ? Math.round((stage.count / totalDeals) * 100) : 0,
    }))
  }, [pipelineStats])

  if (isLoading) {
    return <PerformanceDashboardSkeleton className={className} />
  }

  if (error) {
    return (
      <div className={cn(CARD_CONTAINER_STYLES, 'p-6', className)}>
        <div className="text-center text-gray-500">
          <p>Unable to load performance data</p>
          <p className="text-sm text-gray-400 mt-1">{error}</p>
        </div>
      </div>
    )
  }

  if (!stats) {
    return null
  }

  const { totals, range_info, periods } = stats

  const metricIconStyle = { color: '#97b98f' }

  const performanceMetrics = [
    {
      key: 'deals-opened',
      label: 'Deals Opened',
      icon: <PiTrendUp size={16} style={metricIconStyle} />,
      value: formatInteger(totals.deals_opened),
      tooltipTitle: 'Deals Opened',
      tooltipContent: `Number of deals opened in ${range_info.range_label.toLowerCase()}`,
    },
    {
      key: 'deals-won',
      label: 'Deals Won',
      icon: <PiTrophy size={16} style={metricIconStyle} />,
      value: formatInteger(totals.deals_won),
      tooltipTitle: 'Deals Won',
      tooltipContent: `Deals closed won in ${range_info.range_label.toLowerCase()}`,
    },
    {
      key: 'value-won',
      label: 'Value Won',
      icon: <PiCurrencyDollar size={16} style={metricIconStyle} />,
      value: formatCurrency(totals.value_won),
      tooltipTitle: 'Value Won',
      tooltipContent: `Annual value of deals won in ${range_info.range_label.toLowerCase()}`,
    },
    {
      key: 'win-rate',
      label: 'Win Rate',
      icon: <PiTarget size={16} style={metricIconStyle} />,
      value:
        totals.overall_win_rate !== null
          ? `${formatPercentage(totals.overall_win_rate)}%`
          : '-',
      tooltipTitle: 'Win Rate',
      tooltipContent: `Win rate for ${range_info.range_label.toLowerCase()} (Won / (Won + Lost))`,
    },
  ]

  return (
    <div className={cn(CARD_CONTAINER_STYLES, className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium text-gray-700">
            {range_info.range_label}
          </h3>
          <RangeSelector value={range} onChange={setRange} />
        </div>
        <span className="text-xs text-gray-400">Monthly</span>
      </div>

      {/* Summary Metrics */}
      <div className="mb-4">
        <div className="grid grid-cols-4 gap-3">
          {performanceMetrics.map((metric) => (
            <MetricCard
              key={metric.key}
              icon={metric.icon}
              value={metric.value}
              label={metric.label}
              tooltipTitle={metric.tooltipTitle}
              tooltipContent={metric.tooltipContent}
            />
          ))}
        </div>
      </div>

      {/* Chart */}
      <div>
        <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-2">
          {periodType === 'monthly' ? 'Monthly' : 'Weekly'} Trends
        </div>
        <PerformanceChart periods={periods} />
      </div>

      {/* Pipeline Snapshot */}
      {pipelineStats && (
        <section className="mt-6 border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={() => setShowPipelineSnapshot((prev) => !prev)}
            className="flex w-full items-center justify-between text-sm font-medium text-gray-700"
          >
            <span>Pipeline Snapshot</span>
            <PiCaretDown
              size={16}
              className={cn(
                'transition-transform',
                showPipelineSnapshot ? 'rotate-180' : 'rotate-0'
              )}
            />
          </button>
          {showPipelineSnapshot && (
            <div className="mt-3 space-y-3">
              <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded-lg border border-gray-200 px-3 py-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    Open Deals
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {pipelineStats.open_deals_count}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-200 px-3 py-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    Pipeline Value (1yr)
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatCurrency(pipelineStats.pipeline_total_value)}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-200 px-3 py-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    Lifetime Wins
                  </p>
                  <p className="text-lg font-semibold text-gray-900">
                    {pipelineStats.won_deals_count} won ·{' '}
                    {pipelineStats.lost_deals_count} lost
                  </p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                {pipelineChartItems.length > 0 ? (
                  <ProgressChart
                    label="Pipeline by stage"
                    items={pipelineChartItems}
                    tooltipValueFormatter={(item) =>
                      `${item.value} deal${item.value !== 1 ? 's' : ''}`
                    }
                    legend={{
                      columns: 2,
                      showPercentages: true,
                    }}
                  />
                ) : (
                  <p className="text-sm text-gray-500 text-center">
                    No active deals in your pipeline right now.
                  </p>
                )}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  )
}
